import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from 'src/app/services/api.service';

interface NameCount {
  name: string;
  count: number;
}

interface CollectorDay {
  collector: string;
  day: string;
  count: number;
}

@Component({
  selector: 'app-collector-customers-report',
  templateUrl: './collector-customers-report.component.html',
  styleUrls: ['./collector-customers-report.component.scss'],
})
export class CollectorCustomersReportComponent implements OnInit {
  loading = false;
  tableLoading = false;

  // report aggregates
  totalCustomers = 0;
  totalCollectors = 0;
  totalCompanies = 0;
  byCollector: NameCount[] = [];
  byShirkadda: NameCount[] = [];
  byDay: NameCount[] = [];
  byMonth: NameCount[] = [];
  collectorDaily: CollectorDay[] = [];

  // palette for per-collector stacked bars
  readonly palette = [
    '#00ffcc', '#4fc3f7', '#ffb74d', '#81c784',
    '#ba68c8', '#f06292', '#fff176', '#4db6ac',
  ];

  // view controls
  view: 'daily' | 'monthly' = 'daily';
  days = 30;
  dayOptions = [14, 30, 60, 90];
  chartType: 'line' | 'bar' = 'line';

  // table state
  rows: any[] = [];
  total = 0;
  page = 0;
  size = 15;
  search = '';
  collector = '';
  shirkadda = '';
  sort = 'joinedDate';
  dir: 'asc' | 'desc' = 'desc';

  constructor(private apiService: ApiService, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.loadReport();
    this.loadPage();
  }

  loadReport(): void {
    this.loading = true;
    this.apiService.getCollectorNewCustomersReport().subscribe({
      next: (res: any) => {
        const r = res?.result || res || {};
        this.totalCustomers = r.totalCustomers || 0;
        this.totalCollectors = r.totalCollectors || 0;
        this.totalCompanies = r.totalCompanies || 0;
        this.byCollector = r.byCollector || [];
        this.byShirkadda = r.byShirkadda || [];
        this.byDay = r.byDay || [];
        this.byMonth = r.byMonth || [];
        this.collectorDaily = r.collectorDaily || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Failed to load report', 'Close', { duration: 3000 });
      },
    });
  }

  loadPage(): void {
    this.tableLoading = true;
    this.apiService
      .getCollectorNewCustomersPage(
        this.search, this.collector, this.shirkadda,
        this.sort, this.dir, this.page, this.size
      )
      .subscribe({
        next: (res: any) => {
          const r = res?.result || res || {};
          this.rows = r.rows || [];
          this.total = r.total || 0;
          this.tableLoading = false;
        },
        error: () => {
          this.tableLoading = false;
          this.snackBar.open('Failed to load customers', 'Close', { duration: 3000 });
        },
      });
  }

  refresh(): void {
    this.loadReport();
    this.page = 0;
    this.loadPage();
  }

  applyFilters(): void {
    this.page = 0;
    this.loadPage();
  }

  setView(v: 'daily' | 'monthly'): void {
    this.view = v;
  }

  setDays(d: number): void {
    this.days = d;
  }

  setChart(t: 'line' | 'bar'): void {
    this.chartType = t;
  }

  sortBy(col: string): void {
    if (this.sort === col) {
      this.dir = this.dir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sort = col;
      this.dir = 'desc';
    }
    this.page = 0;
    this.loadPage();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.size));
  }

  nextPage(): void {
    if (this.page < this.totalPages - 1) {
      this.page++;
      this.loadPage();
    }
  }

  prevPage(): void {
    if (this.page > 0) {
      this.page--;
      this.loadPage();
    }
  }

  get topCollector(): string {
    if (!this.byCollector.length) { return '—'; }
    const top = [...this.byCollector].sort((a, b) => b.count - a.count)[0];
    return top ? top.name : '—';
  }

  // ---- chart data ----
  get chartSeries(): NameCount[] {
    const src = this.view === 'daily' ? this.byDay : this.byMonth;
    if (this.view === 'monthly') {
      return src.slice(-12);
    }
    return src.slice(-this.days);
  }

  get chartMax(): number {
    const m = Math.max(...this.chartSeries.map((s) => s.count), 0);
    return m > 0 ? m : 1;
  }

  // SVG line-chart helpers (viewBox 0 0 600 200)
  get linePoints(): string {
    const s = this.chartSeries;
    if (!s.length) { return ''; }
    const w = 600, h = 200, pad = 10;
    const step = s.length > 1 ? (w - pad * 2) / (s.length - 1) : 0;
    return s
      .map((p, i) => {
        const x = pad + i * step;
        const y = h - pad - (p.count / this.chartMax) * (h - pad * 2);
        return `${x},${y}`;
      })
      .join(' ');
  }

  get lineDots(): { x: number; y: number }[] {
    const s = this.chartSeries;
    const w = 600, h = 200, pad = 10;
    const step = s.length > 1 ? (w - pad * 2) / (s.length - 1) : 0;
    return s.map((p, i) => ({
      x: pad + i * step,
      y: h - pad - (p.count / this.chartMax) * (h - pad * 2),
    }));
  }

  barWidth(): number {
    const n = this.chartSeries.length || 1;
    return Math.max(4, Math.floor(560 / n) - 4);
  }

  barX(i: number): number {
    const n = this.chartSeries.length || 1;
    return 20 + i * (560 / n);
  }

  barHeight(count: number): number {
    return (count / this.chartMax) * 170;
  }

  barY(count: number): number {
    return 190 - this.barHeight(count);
  }

  // ---- per-collector daily horizontal chart ----
  // Each collector is a row (name aligned on the left); the bar is segmented
  // by day so you can see how many customers they added each day.
  get dailyDays(): string[] {
    const set = new Set<string>();
    for (const r of this.collectorDaily) {
      if (r.day) { set.add(r.day); }
    }
    return Array.from(set).sort();
  }

  get collectorNames(): string[] {
    const totals = new Map<string, number>();
    for (const r of this.collectorDaily) {
      totals.set(r.collector, (totals.get(r.collector) || 0) + r.count);
    }
    return Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .map((e) => e[0]);
  }

  collectorTotal(name: string): number {
    return this.collectorDaily
      .filter((r) => r.collector === name)
      .reduce((s, r) => s + r.count, 0);
  }

  get collectorMax(): number {
    const m = Math.max(...this.collectorNames.map((n) => this.collectorTotal(n)), 0);
    return m > 0 ? m : 1;
  }

  dayColor(day: string): string {
    const i = this.dailyDays.indexOf(day);
    return this.palette[(i < 0 ? 0 : i) % this.palette.length];
  }

  // horizontal stacked segments for one collector, one segment per day.
  // bar area starts at x=200, max width 360.
  collectorSegs(name: string): { day: string; count: number; x: number; w: number; color: string }[] {
    const segs: { day: string; count: number; x: number; w: number; color: string }[] = [];
    let x = 200;
    for (const day of this.dailyDays) {
      const rec = this.collectorDaily.find((r) => r.collector === name && r.day === day);
      if (!rec || rec.count <= 0) { continue; }
      const w = (rec.count / this.collectorMax) * 360;
      segs.push({ day, count: rec.count, x, w, color: this.dayColor(day) });
      x += w;
    }
    return segs;
  }

  collectorRowY(i: number): number {
    return 14 + i * 34;
  }

  get collectorChartHeight(): number {
    return 20 + this.collectorNames.length * 34;
  }
}
